"use client";

import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { FirebaseApp } from "firebase/app";
import { Database } from "firebase/database";
import { Auth, User } from "firebase/auth";
import { useSession } from "next-auth/react";
import { usePathname } from "next/navigation";
import type { Session } from "next-auth";
import { toast } from "react-toastify";
import { getFirebaseApp, getFirebaseAuth, getFirebaseDatabase } from "@/app/lib/firebaseClient";
import { signInWithCustomToken } from "firebase/auth";
import { FIREBASE_APP_INSTANCE_NAMES, PROVIDER_TYPES } from "@/app/lib/constants";
import { useUserContext } from "@/context/useUserContext";
import { isSpecificProviderType } from "@/app/lib/utils";

interface AdminSupportChatProviderContextType {
  app: FirebaseApp | null;
  database: Database | null;
  auth: Auth | null;
  user: User | null;
  isAuthenticated: boolean;
  isInitializing: boolean;
}

const AdminSupportChatProviderContext = createContext<AdminSupportChatProviderContextType>({
  app: null,
  database: null,
  auth: null,
  user: null,
  isAuthenticated: false,
  isInitializing: true,
});

export const useFirebaseAdmin = () => useContext(AdminSupportChatProviderContext);

interface AdminSupportChatProviderProps {
  children: ReactNode;
}

export const AdminSupportChatProvider: React.FC<AdminSupportChatProviderProps> = ({ children }) => {
  const [app, setApp] = useState<FirebaseApp | null>(null);
  const [database, setDatabase] = useState<Database | null>(null);
  const [auth, setAuth] = useState<Auth | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  
  const { data: session } = useSession();
  const pathname = usePathname();
  const { role, providerType } = useUserContext();
  
  // Check if current path is an admin chat page
  const isAdminChatPage = pathname && (pathname === '/admin/chat' || pathname.startsWith('/admin/chat'));
  
  // Check if user is LinqOps Support Provider (validates both role range and provider type)
  const isLinqopsSupportProvider = isSpecificProviderType(
    role?.toString(),
    providerType,
    PROVIDER_TYPES.LINQOPS_SUPPORT
  );

  // Initialize Firebase only on admin chat pages for LinqOps Support Provider users
  useEffect(() => {
    // Skip Firebase initialization if not on an admin chat page or user is not LinqOps Support Provider
    if (!isAdminChatPage || !isLinqopsSupportProvider) {
      setIsInitializing(false);
      return;
    }
    
    try {
      const firebaseApp = getFirebaseApp(FIREBASE_APP_INSTANCE_NAMES.ADMIN_SUPPORT);
      const db = getFirebaseDatabase(FIREBASE_APP_INSTANCE_NAMES.ADMIN_SUPPORT);
      const authInstance = getFirebaseAuth(FIREBASE_APP_INSTANCE_NAMES.ADMIN_SUPPORT);

      setApp(firebaseApp);
      setDatabase(db);
      setAuth(authInstance);
      
      // Listen for auth state changes
      const unsubscribe = authInstance.onAuthStateChanged((currentUser: User | null) => {
        setUser(currentUser);
        setIsAuthenticated(!!currentUser);
        setIsInitializing(false);
        
        // If no Firebase user exists but we have a NextAuth session, fetch a token
        if (!currentUser && session) {
          fetchAndSignInWithFirebaseAdminSupportToken(authInstance);
        }
      });
      
      return () => unsubscribe();
    } catch (error) {
      console.error("Error initializing Firebase admin:", error);
      toast.error("Failed to initialize Firebase for admin chat");
      setIsInitializing(false);
    }
  }, [session, isAdminChatPage, isLinqopsSupportProvider]);
  
  // Function to fetch Firebase token and sign in for admin
  const fetchAndSignInWithFirebaseAdminSupportToken = async (authInstance: Auth) => {
    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/admin/chat/support-firebase-token`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch Firebase token: ${response.status}`);
      }
      
      const data = await response.json();
      const firebaseToken = data.firebaseToken;
      
      if (firebaseToken) {
        const userCredential = await signInWithCustomToken(authInstance, firebaseToken);
        setUser(userCredential.user);
        setIsAuthenticated(true);
      } else {
        throw new Error("No Firebase token returned from API");
      }
    } catch (error) {
      console.error("Error signing in with Firebase admin token:", error);
      toast.error("Failed to authenticate with Firebase for admin chat");
      setIsInitializing(false);
    }
  };  
  const value = {
    app,
    database,
    auth,
    user,
    isAuthenticated,
    isInitializing,
  };
  
  return (
    <AdminSupportChatProviderContext.Provider value={value}>
      {children}
    </AdminSupportChatProviderContext.Provider>
  );
};
